// The Features tab of the DevToolsDialog — the v3 FeaturesModal capability
// (toggle the org's feature OVERRIDES) recomposed native. Write wire = the
// live OrgFeaturesTab shape: PUT orgs/:id.json { id, org: { id,
// org_features_attributes } } + the orgs detail-cache patch. Toggle-only (the
// v3 modal had no add affordance); badges show the org's effective
// enabled_features state.
import { formatJsonApiData } from '@/lib/commonHelpers';
import { useCustomMutation, useNotification, useOne } from '@refinedev/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { useOrgCacheUpdate } from '@/pages/orgs/hooks/useOrgCacheUpdate';

export const DevToolsFeaturesPanel = () => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const currentUser = useCurrentUser();
  const orgId = currentUser?.org_id;
  const { mutate, isPending } = useCustomMutation();
  const { updateOrgCacheWithData } = useOrgCacheUpdate();

  // Same fetch as the v3 modal (BaseForm edit → GET orgs/:id).
  const { data, isLoading } = useOne({
    resource: 'orgs',
    id: orgId,
    queryOptions: { enabled: !!orgId },
  });
  const org: any = data?.data;

  const [orgFeatures, setOrgFeatures] = useState<any[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (org?.org_features) setOrgFeatures(formatJsonApiData(org.org_features));
  }, [org?.org_features]);

  const enabledFeatures = org?.enabled_features ?? {};
  const query = search.trim().toLowerCase();
  const visibleFeatures = query
    ? orgFeatures.filter((f) =>
        String(f.feature_name ?? '')
          .toLowerCase()
          .includes(query)
      )
    : orgFeatures;

  const handleToggleEnabled = (featureId: string | number, enabled: boolean) => {
    setOrgFeatures((previous) =>
      previous.map((f) => (f.feature_id === featureId ? { ...f, enabled } : f))
    );
  };

  const saveFeatures = () => {
    mutate(
      {
        url: `orgs/${orgId}.json`,
        method: 'put',
        values: { id: orgId, org: { id: orgId, org_features_attributes: orgFeatures } },
      },
      {
        onSuccess: (response: any) => {
          open?.({ type: 'success', message: t('OrgFeatures.features_saved_successfully') });
          const savedOrgFeatures = response?.data?.org?.data?.attributes?.org_features;
          if (savedOrgFeatures) {
            setOrgFeatures(formatJsonApiData(savedOrgFeatures));
            updateOrgCacheWithData(savedOrgFeatures, orgId, response, 'org_features');
          }
        },
        onError: () => {
          open?.({ type: 'error', message: t('OrgFeatures.failed_to_save_features') });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="devtools-features-loading">
        <Spinner aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="devtools-features">
      <div className="flex items-center justify-between gap-4">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('DevToolsDialog.search_features')}
          className="max-w-64"
          data-testid="devtools-features-search"
        />
        <Button
          type="button"
          onClick={saveFeatures}
          disabled={isPending}
          data-testid="devtools-features-save"
        >
          {isPending && <Spinner aria-hidden="true" />}
          {t('CommonText.save')}
        </Button>
      </div>

      <div
        className="flex flex-col divide-y rounded-lg border bg-card shadow-sm"
        data-testid="devtools-features-list"
      >
        {visibleFeatures.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('CommonText.no_data')}
          </p>
        ) : (
          visibleFeatures.map((feature) => (
            <div
              key={feature.feature_id}
              className="flex items-center justify-between gap-4 px-4 py-2.5"
              data-testid={`devtools-feature-${feature.feature_id}`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm">{feature.feature_name}</span>
                <Badge variant={enabledFeatures[feature.feature_name] ? 'default' : 'outline'}>
                  {enabledFeatures[feature.feature_name]
                    ? t('DevToolsDialog.active')
                    : t('DevToolsDialog.inactive')}
                </Badge>
              </div>
              <Switch
                checked={feature.enabled ?? true}
                disabled={isPending}
                onCheckedChange={(checked) => handleToggleEnabled(feature.feature_id, checked)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};
